/**
 * Product star ratings from the Yotpo metafield snapshots.
 *
 * Owns every star bottomline in the theme. The Yotpo app writes a bottomline
 * snapshot to the `yotpo.preloaded_bottomline` product metafield; Liquid
 * renders scaffolds settled wherever it has the product, and client-built
 * cards call `prefill` with the snapshot carried by Athos results
 * (`mfield_yotpo_preloaded_bottomline`) or the `?view=json` product endpoint.
 * A product without a snapshot has no reviews — its scaffold collapses to the
 * empty state; this module makes no network requests. The MutationObserver
 * settles any injected `[data-js="yotpo-rating"]` scaffold that arrives
 * unresolved, keyed on the Shopify numeric product id.
 *
 * Loaded with `defer` immediately after `product-card-bind.js` so the
 * MutationObserver is live before any hydrator injects cards.
 */
(function () {
  'use strict';
  if (window.YotpoRatings) return;

  const SELECTOR = '[data-js="yotpo-rating"]:not([data-initialised="true"])';

  const memory = new Map();

  /**
   * Normalise the Yotpo bottomline payload.
   * @param {object} json
   * @returns {{ count: number, average: number, percentage: number } | null} Null when no reviews.
   */
  function normalise(json) {
    const bottomline = json && json.bottomline;
    const count = bottomline ? Number(bottomline.totalReviews) : 0;
    if (!count) return null;
    const average = Number(bottomline.averageScore) || 0;
    return { count: count, average: average, percentage: Math.round((average / 5) * 100) };
  }

  /**
   * Build the accessible label from the scaffold's template and resolved values.
   * @param {HTMLElement} root
   * @param {object} data
   * @returns {string}
   */
  function buildLabel(root, data) {
    const template = root.getAttribute('data-label-template');
    if (!template) return '';
    return template.replace('{score}', data.average.toFixed(1)).replace('{count}', String(data.count));
  }

  /**
   * Populate a scaffold with a resolved rating, or collapse it when there are none.
   * @param {HTMLElement} root
   * @param {object | null} data
   * @returns {void}
   */
  function populate(root, data) {
    root.dataset.initialised = 'true';

    if (!data) {
      root.dataset.state = 'empty';
      return;
    }

    const stars = root.querySelector('[data-js="yotpo-rating-stars"]');
    if (stars) stars.style.setProperty('--rating-percentage', data.percentage + '%');

    const count = root.querySelector('[data-js="yotpo-rating-count"]');
    if (count) count.textContent = String(data.count);

    const average = root.querySelector('[data-js="yotpo-rating-average"]');
    if (average) average.textContent = data.average.toFixed(1);

    const label = buildLabel(root, data);
    if (label) root.setAttribute('aria-label', label);

    root.dataset.state = 'ready';
  }

  /**
   * Settle a scaffold from a bottomline snapshot — the Yotpo metafield value
   * carried by Athos result fields (`mfield_yotpo_preloaded_bottomline`) or
   * the `?view=json` product endpoint. Accepts the parsed object or the raw
   * JSON string (possibly HTML-entity-escaped by the feed); a missing or
   * unparseable snapshot means no reviews and collapses the scaffold. Seeds
   * the in-memory cache so scaffolds injected later for the same product
   * settle without a fresh snapshot.
   * @param {HTMLElement} root
   * @param {string | object | null} raw
   * @returns {void}
   */
  function prefill(root, raw) {
    if (!root) return;
    let json = raw;
    if (typeof raw === 'string' && raw !== '') {
      try {
        json = JSON.parse(raw.replace(/&quot;/g, '"').replace(/&amp;/g, '&'));
      } catch (err) {
        json = null;
      }
    }
    const data = json && typeof json === 'object' && json.bottomline ? normalise(json) : null;
    const id = resolveId(root);
    if (id) memory.set(id, data);
    populate(root, data);
  }

  /**
   * Resolve the product id for a scaffold — its own attribute, else the enclosing card.
   * @param {HTMLElement} root
   * @returns {string}
   */
  function resolveId(root) {
    if (root.dataset.productId) return root.dataset.productId;
    const card = root.closest('[data-js="product-card"]');
    return card ? card.dataset.productId || '' : '';
  }

  /**
   * Settle a single scaffold from the cache, empty on a miss. Idempotent.
   * @param {HTMLElement} root
   * @returns {void}
   */
  function hydrate(root) {
    if (root.dataset.initialised === 'true') return;
    populate(root, memory.get(resolveId(root)) || null);
  }

  /**
   * Find and settle every unresolved scaffold within a scope (self included).
   * @param {Document | HTMLElement} [scope]
   * @returns {void}
   */
  function scan(scope) {
    const root = scope || document;
    if (root.nodeType === 1 && root.matches && root.matches(SELECTOR)) hydrate(root);
    root.querySelectorAll(SELECTOR).forEach(hydrate);
  }

  scan(document);

  if (document.body) {
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) scan(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  window.YotpoRatings = Object.freeze({ hydrate: hydrate, scan: scan, prefill: prefill });
})();
